// Shared SEO scoring + helpers for AleaEdge article pages and admin console.
// UI-only logic — does not modify generation, publishing, or permissions.

export type SeoLevel = "ok" | "warn" | "fail";
export type SeoCheck = {
  key: string;
  label: string;
  level: SeoLevel;
  hint?: string;
  fix?: string;
  weight?: number;
};

export type SeoArticleLike = {
  id: string;
  slug: string;
  title: string;
  meta_title?: string | null;
  meta_description?: string | null;
  excerpt?: string | null;
  article_body_md?: string | null;
  ai_generated_at?: string | null;
  reviewed_at?: string | null;
  published_at?: string | null;
  pick_snapshot?: any;
  sport?: string | null;
};

const STOP = new Set([
  "the","a","an","and","or","but","of","to","in","on","for","with","at","by","from",
  "is","are","was","were","be","been","being","this","that","these","those","it","as","vs",
]);

function tokens(text: string): string[] {
  return (text || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((t) => t.length > 2 && !STOP.has(t));
}

export function wordCount(md: string | null | undefined): number {
  if (!md) return 0;
  const stripped = md.replace(/```[\s\S]*?```/g, " ").replace(/[#>*_`~\-]/g, " ");
  const t = stripped.trim();
  return t ? t.split(/\s+/).length : 0;
}

export function readingTimeMin(words: number): number {
  return Math.max(1, Math.round(words / 220));
}

export function countInternalLinks(md: string | null | undefined): number {
  if (!md) return 0;
  return (md.match(/\]\(\/[^)]+\)/g) ?? []).length;
}

export function avgWordsPerSentence(md: string | null | undefined): number {
  if (!md) return 0;
  const sentences = md.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  if (!sentences.length) return 0;
  return wordCount(md) / sentences.length;
}

export function suggestSlug(sport: string | null | undefined, teams: string | null | undefined, dateIso: string | null | undefined): string {
  const s = (sport || "sports").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const t = (teams || "matchup").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const d = dateIso ? new Date(dateIso).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
  return `${s}-picks-today/${t}-prediction-${d}`;
}

export function structuredDataReady(article: SeoArticleLike): {
  article: boolean;
  sportsEvent: boolean;
  breadcrumb: boolean;
} {
  const snap = article.pick_snapshot ?? {};
  return {
    article: !!(article.title && (article.meta_description || article.excerpt) && article.published_at),
    sportsEvent: !!(snap.teams && (snap.game_start_time || snap.game_time) && (article.sport ?? snap.sport)),
    breadcrumb: !!(article.sport && article.slug?.includes("/")),
  };
}

const SCORE_BANDS: Array<{ min: number; label: string; tone: string }> = [
  { min: 90, label: "Excellent", tone: "text-primary" },
  { min: 75, label: "Strong", tone: "text-primary" },
  { min: 60, label: "Fair", tone: "text-accent" },
  { min: 0, label: "Poor", tone: "text-destructive" },
];
export function seoBand(score: number) {
  return SCORE_BANDS.find((b) => score >= b.min) ?? SCORE_BANDS[SCORE_BANDS.length - 1];
}

export type SeoResult = {
  checks: SeoCheck[];
  score: number;
  band: { label: string; tone: string };
  words: number;
  readMin: number;
  internalLinks: number;
  okCount: number;
  warnCount: number;
  failCount: number;
};

export function computeArticleSeo(
  article: SeoArticleLike | null,
  allArticles: SeoArticleLike[] = [],
): SeoResult {
  if (!article) {
    return {
      checks: [], score: 0, band: seoBand(0),
      words: 0, readMin: 0, internalLinks: 0,
      okCount: 0, warnCount: 0, failCount: 0,
    };
  }
  const body = article.article_body_md ?? "";
  const words = wordCount(body);
  const internalLinks = countInternalLinks(body);
  const avg = avgWordsPerSentence(body);
  const slugValid = /^[a-z0-9]+(?:[-/][a-z0-9]+)*$/.test(article.slug);
  const slugRichness = article.slug.includes("/") && /prediction|preview|picks/.test(article.slug);
  const md = (article.meta_description ?? "").trim();
  const mt = (article.meta_title ?? article.title ?? "").trim();
  const dupCount = allArticles.filter(
    (a) => a.id !== article.id && a.title.trim().toLowerCase() === article.title.trim().toLowerCase(),
  ).length;

  // Keyword coverage: how many top body tokens appear in title + meta
  const bodyTokens = tokens(body);
  const freq = new Map<string, number>();
  bodyTokens.forEach((t) => freq.set(t, (freq.get(t) ?? 0) + 1));
  const topKeywords = [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k]) => k);
  const meta = `${mt} ${md}`.toLowerCase();
  const covered = topKeywords.filter((k) => meta.includes(k)).length;
  const coverageRatio = topKeywords.length ? covered / topKeywords.length : 0;

  // Freshness
  const lastTouch = new Date(article.published_at ?? article.reviewed_at ?? article.ai_generated_at ?? Date.now()).getTime();
  const daysOld = (Date.now() - lastTouch) / 86_400_000;

  const sd = structuredDataReady(article);
  const sdReady = [sd.article, sd.sportsEvent, sd.breadcrumb].filter(Boolean).length;

  const checks: SeoCheck[] = [
    {
      key: "title", label: "Title quality", weight: 10,
      level: mt.length >= 30 && mt.length <= 60 ? "ok" : mt.length > 0 ? "warn" : "fail",
      hint: `${mt.length}/60`, fix: "Aim for 30–60 chars including a keyword.",
    },
    {
      key: "slug", label: "Slug quality", weight: 10,
      level: slugValid && slugRichness ? "ok" : slugValid ? "warn" : "fail",
      hint: slugValid ? (slugRichness ? "keyword-rich" : "valid") : "invalid",
      fix: "Use sport/team-vs-team-prediction-YYYY-MM-DD.",
    },
    {
      key: "meta", label: "Meta description", weight: 10,
      level: md.length >= 110 && md.length <= 160 ? "ok" : md.length >= 60 ? "warn" : "fail",
      hint: `${md.length}/160`, fix: "Write 110–160 chars summarizing the matchup.",
    },
    {
      key: "len", label: "Article length", weight: 15,
      level: words >= 800 ? "ok" : words >= 500 ? "warn" : "fail",
      hint: `${words} words`, fix: "Target ~800+ words for depth.",
    },
    {
      key: "links", label: "Internal links", weight: 10,
      level: internalLinks >= 3 ? "ok" : internalLinks >= 1 ? "warn" : "fail",
      hint: `${internalLinks} link${internalLinks === 1 ? "" : "s"}`,
      fix: "Add 3+ internal links (Top 5, History, sport hub).",
    },
    {
      key: "read", label: "Readability", weight: 10,
      level: avg > 0 && avg <= 22 ? "ok" : avg > 0 && avg <= 28 ? "warn" : "fail",
      hint: avg ? `${avg.toFixed(1)} w/sent` : "—",
      fix: "Keep average sentence length ≤22 words.",
    },
    {
      key: "dup", label: "Duplicate risk", weight: 5,
      level: dupCount === 0 ? "ok" : dupCount === 1 ? "warn" : "fail",
      hint: dupCount ? `${dupCount} title match${dupCount === 1 ? "" : "es"}` : "unique",
      fix: "Differentiate the title from existing articles.",
    },
    {
      key: "schema", label: "Structured data", weight: 10,
      level: sdReady === 3 ? "ok" : sdReady >= 2 ? "warn" : "fail",
      hint: `${sdReady}/3 ready`,
      fix: "Article + SportsEvent + Breadcrumb schemas required.",
    },
    {
      key: "kw", label: "Keyword coverage", weight: 10,
      level: coverageRatio >= 0.5 ? "ok" : coverageRatio >= 0.25 ? "warn" : "fail",
      hint: `${Math.round(coverageRatio * 100)}%`,
      fix: "Include top body keywords in title and meta description.",
    },
    {
      key: "fresh", label: "Freshness", weight: 10,
      level: daysOld <= 3 ? "ok" : daysOld <= 14 ? "warn" : "fail",
      hint: daysOld < 1 ? "today" : `${Math.round(daysOld)}d old`,
      fix: "Refresh content within 3 days of game.",
    },
  ];

  const totalWeight = checks.reduce((s, c) => s + (c.weight ?? 1), 0);
  const earned = checks.reduce((s, c) => {
    const w = c.weight ?? 1;
    return s + (c.level === "ok" ? w : c.level === "warn" ? w * 0.5 : 0);
  }, 0);
  const score = Math.round((earned / totalWeight) * 100);

  return {
    checks,
    score,
    band: seoBand(score),
    words,
    readMin: readingTimeMin(words),
    internalLinks,
    okCount: checks.filter((c) => c.level === "ok").length,
    warnCount: checks.filter((c) => c.level === "warn").length,
    failCount: checks.filter((c) => c.level === "fail").length,
  };
}

export const SEO_DOT: Record<SeoLevel, string> = {
  ok: "bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.6)]",
  warn: "bg-accent shadow-[0_0_8px_hsl(var(--accent)/0.6)]",
  fail: "bg-destructive shadow-[0_0_8px_hsl(var(--destructive)/0.6)]",
};
